import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { renderToStaticMarkup } from "react-dom/server";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FinalCta } from "@/components/site/FinalCta";
import { InlineQuoteCta } from "@/components/site/InlineQuoteCta";
import { GROUPS, FAQ_PAGE_REDIRECTS, findFaq, faqSlug } from "@/lib/faq-data";
import { withQuery } from "@/lib/redirect-query";
import { FAQ_META_TITLES } from "@/lib/faq-meta-titles";
import { brandedTitle, htmlToText, snippet } from "@/lib/meta-text";
import { breadcrumbSchema, safeJsonLd } from "@/lib/structured-data";

function faqText(slug: string): { question: string; answer: string } {
  const match = findFaq(slug);
  if (!match) return { question: "", answer: "" };
  return { question: match.faq.q, answer: htmlToText(renderToStaticMarkup(<>{match.faq.a}</>)) };
}

export const Route = createFileRoute("/faq/$slug")({
  beforeLoad: ({ params, location }) => {
    // Questions merged into a longer article: one 301 there, query kept.
    const merged = FAQ_PAGE_REDIRECTS[params.slug.toLowerCase().replace(/^\/+|\/+$/g, "")];
    if (merged) {
      throw redirect({ href: withQuery(merged, location.searchStr), statusCode: 301 });
    }
    const match = findFaq(params.slug);
    if (!match) {
      throw redirect({ href: "/hair-transplant-faqs/", statusCode: 301 });
    }
    // Legacy aliases resolve to one canonical question URL.
    if (match.slug !== params.slug) {
      throw redirect({ href: `/faq/${match.slug}/`, statusCode: 301 });
    }
  },
  loader: ({ params }) => faqText(params.slug),
  head: ({ params, loaderData }) => {
    const question = loaderData?.question || "Hair Transplant FAQ";
    const shortTitle = FAQ_META_TITLES[params.slug] ?? question;
    // Drop the brand suffix when it would push the title past 60 characters.
    const title = brandedTitle(shortTitle, "RESTORE® Hair");
    // The start of the physicians' answer. The old template, "<question> —
    // answered by ...", was under 110 characters on short questions.
    const description = loaderData?.answer
      ? snippet(loaderData.answer)
      : `${question} Answered by the hair restoration physicians at RESTORE Hair.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [
        { rel: "canonical", href: `https://restorehair.com/faq/${params.slug}/` },
      ],
    };
  },
  component: FaqDetailPage,
});

const RELATED_FAQ_COUNT = 6;

function FaqDetailPage() {
  const { slug } = Route.useParams();
  const match = findFaq(slug);
  if (!match) return null;

  const group = GROUPS[match.groupIndex];
  // The questions that follow this one in its group, with wrap. The first six
  // of each group used to be the only related questions, so the rest had one
  // internal link (the FAQ hub). With the rotation every question gets links
  // from up to six other questions.
  // Questions whose page redirects (FAQ_PAGE_REDIRECTS) are left out.
  const items = (group?.items ?? []).filter(
    (f) => faqSlug(f.q) === match.slug || !(faqSlug(f.q) in FAQ_PAGE_REDIRECTS),
  );
  const self = items.findIndex((f) => faqSlug(f.q) === match.slug);
  const related = Array.from({ length: Math.min(RELATED_FAQ_COUNT, items.length - 1) }, (_, k) =>
    items[(self + 1 + k) % items.length],
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: match.faq.q,
        acceptedAnswer: { "@type": "Answer", text: htmlToText(renderToStaticMarkup(<>{match.faq.a}</>)) },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto max-w-3xl px-4 lg:px-8">
            <nav className="mb-6 text-sm text-muted-foreground">
              <Link to="/hair-transplant-faqs/" className="underline hover:text-foreground">
                Hair Transplant FAQs
              </Link>
              <span className="mx-2">/</span>
              <span>{group?.title}</span>
            </nav>
            <h1 className="text-3xl font-extrabold leading-tight text-foreground md:text-4xl">
              {match.faq.q}
            </h1>
            <div className="mt-6 text-base leading-relaxed text-muted-foreground">
              {match.faq.a}
            </div>

            <InlineQuoteCta />

            {related.length > 0 && (
              <div className="mt-12 border-t border-border pt-8">
                <h2 className="mb-6 text-xl font-bold text-foreground">Related questions</h2>
                <div className="space-y-8">
                  {related.map((f) => (
                    <div key={f.q}>
                      <h3 className="text-lg font-bold text-foreground">
                        <Link
                          to="/faq/$slug/"
                          params={{ slug: faqSlug(f.q) }}
                          className="text-primary underline underline-offset-2 hover:opacity-80"
                        >
                          {f.q}
                        </Link>
                      </h3>
                      <div className="mt-2 text-base leading-relaxed text-muted-foreground">{f.a}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10">
              <Link
                to="/hair-transplant-faqs/"
                className="inline-block bg-primary px-8 py-4 text-base font-bold text-primary-foreground transition hover:opacity-90"
              >
                See all hair transplant FAQs
              </Link>
            </div>
          </div>
        </section>
        <FinalCta variant="light" />
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: safeJsonLd(
            breadcrumbSchema([
              { name: "Hair Transplant FAQs", path: "/hair-transplant-faqs/" },
              { name: match.faq.q, path: `/faq/${match.slug}/` },
            ]),
          ),
        }}
      />
    </div>
  );
}
